import React, { useEffect, useState } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { getApiUrl } from '../utils/api';
import './Executions.css';

const EXECUTION_LIMITS = {
  name: 150,
  description: 1000
};

/**
 * Wizard inline (sem modal) para criar uma execução a partir de um ciclo já
 * conhecido — usado no Repositório de Testes > Ciclo de Testes, pra não sair
 * da página. Projeto e ciclo vêm fixos por props (não são escolhidos aqui).
 */
const CreateExecutionWizard = ({ projectId, projectName, cycleId, cycleName, onCreated, onCancel }) => {
  const { showError, showSuccess } = useNotification();

  const [step, setStep] = useState(1);
  const [executionName, setExecutionName] = useState('');
  const [executionDescription, setExecutionDescription] = useState('');
  const [testCases, setTestCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nameError, setNameError] = useState('');

  // Adicionar casos de teste existentes (do repositório) ao ciclo
  const [availableCases, setAvailableCases] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addFolderFilter, setAddFolderFilter] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState(() => new Set());
  const [addingCases, setAddingCases] = useState(false);

  const loadCycleCases = async () => {
    if (!cycleId) return;
    setLoadingCases(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`test-repository/test-cycles/${cycleId}/cases`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setTestCases(list);
      }
    } catch (error) {
      console.error('Erro ao carregar casos do ciclo:', error);
    } finally {
      setLoadingCases(false);
    }
  };

  const loadAvailableCases = async () => {
    if (!projectId) return;
    setLoadingAvailable(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`test-repository/test-cases?project_id=${projectId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setAvailableCases(list);
      }
    } catch (error) {
      console.error('Erro ao carregar casos do repositório:', error);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const loadFolders = async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`test-repository/folders?project_id=${projectId}&scope=cases`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setFolders(list);
      }
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
    }
  };

  useEffect(() => {
    loadCycleCases();
    loadAvailableCases();
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, projectId]);

  const toggleSelectToAdd = (caseId) => {
    setSelectedToAdd(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId); else next.add(caseId);
      return next;
    });
  };

  const handleAddSelectedCases = async () => {
    if (selectedToAdd.size === 0 || addingCases) return;
    setAddingCases(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`test-repository/test-cycles/${cycleId}/cases`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ case_ids: Array.from(selectedToAdd) })
      });
      if (response.ok) {
        showSuccess('Casos adicionados ao ciclo');
        setSelectedToAdd(new Set());
        await loadCycleCases();
      } else {
        const error = await response.json();
        showError(error.error || error.message || 'Erro ao adicionar casos ao ciclo');
      }
    } catch (error) {
      console.error('Erro ao adicionar casos ao ciclo:', error);
      showError('Erro ao adicionar casos ao ciclo');
    } finally {
      setAddingCases(false);
    }
  };

  const linkedIds = new Set(testCases.map(tc => String(tc.id)));
  const addableCases = availableCases
    .filter(tc => !linkedIds.has(String(tc.id)))
    .filter(tc => !addFolderFilter || String(tc.folder_id || '') === String(addFolderFilter))
    .filter(tc => !addSearch.trim() || String(tc.title || '').toLowerCase().includes(addSearch.trim().toLowerCase()));

  const selectAllVisible = () => {
    setSelectedToAdd(prev => {
      const next = new Set(prev);
      addableCases.forEach(tc => next.add(tc.id));
      return next;
    });
  };

  const clearVisibleSelection = () => {
    setSelectedToAdd(prev => {
      const next = new Set(prev);
      addableCases.forEach(tc => next.delete(tc.id));
      return next;
    });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!executionName.trim()) {
        setNameError('Nome da execução é obrigatório');
        showError('Preencha o nome da execução');
        return;
      }
      setNameError('');
      setStep(2);
      return;
    }
    if (step === 2) {
      if (testCases.length === 0) {
        showError('Este ciclo não tem casos de teste vinculados.');
        return;
      }
      setStep(3);
    }
  };

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('executions/runs'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: executionName,
          description: executionDescription,
          project_id: projectId,
          cycle_id: cycleId,
          included_cases: testCases.map(tc => tc.id)
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess('Execução criada com sucesso');
        onCreated && onCreated(result?.data || null);
      } else {
        const error = await response.json();
        showError(error.error || error.message || 'Erro ao criar execução');
      }
    } catch (error) {
      console.error('Erro ao criar execução:', error);
      showError('Erro ao criar execução');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="cycle-cases-inline">
      <div className="modal-header">
        <h2 style={{ margin: 0 }}>Nova Execução — {cycleName}</h2>
      </div>
      <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 24 }}>
        <div className="wizard-rail">
          <div className={`wizard-rail-item${step === 1 ? ' active' : step > 1 ? ' done' : ''}`}>
            <div className="wizard-rail-num">{step > 1 ? '✓' : '1'}</div>
            <div>
              <div className="wizard-rail-title">Detalhes</div>
              <div className="wizard-rail-sub">Nome da execução</div>
            </div>
          </div>
          <div className="wizard-rail-line" />
          <div className={`wizard-rail-item${step === 2 ? ' active' : step > 2 ? ' done' : ''}`}>
            <div className="wizard-rail-num">{step > 2 ? '✓' : '2'}</div>
            <div>
              <div className="wizard-rail-title">Casos de teste</div>
              <div className="wizard-rail-sub">{testCases.length > 0 ? `${testCases.length} do ciclo` : 'Do ciclo selecionado'}</div>
            </div>
          </div>
          <div className="wizard-rail-line" />
          <div className={`wizard-rail-item${step === 3 ? ' active' : ''}`}>
            <div className="wizard-rail-num">3</div>
            <div>
              <div className="wizard-rail-title">Revisar e criar</div>
              <div className="wizard-rail-sub">Confirme e finalize</div>
            </div>
          </div>
        </div>

        <div>
          {step === 1 && (
            <>
              <div className="form-group">
                <label>Projeto</label>
                <input type="text" value={projectName || ''} disabled />
              </div>
              <div className="form-group">
                <label>Ciclo</label>
                <input type="text" value={cycleName || ''} disabled />
              </div>
              <div className="form-group">
                <label>Nome da Execução *</label>
                <input
                  type="text"
                  value={executionName}
                  onChange={(e) => { setExecutionName(e.target.value); if (nameError) setNameError(''); }}
                  maxLength={EXECUTION_LIMITS.name}
                  placeholder="Digite o nome da execução"
                  className={nameError ? 'error' : undefined}
                  aria-invalid={!!nameError}
                />
                {nameError && <div className="field-error">{nameError}</div>}
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={executionDescription}
                  onChange={(e) => setExecutionDescription(e.target.value)}
                  maxLength={EXECUTION_LIMITS.description}
                  placeholder="Descrição opcional"
                  rows="3"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, color: '#6c757d' }}>
                Casos já vinculados ao ciclo <strong>{cycleName}</strong> — serão incluídos automaticamente na execução.
              </div>
              <div className="wizard-case-preview" style={{ marginBottom: 20 }}>
                {loadingCases && (
                  <div style={{ padding: 16, fontSize: 13, color: '#6c757d' }}>Carregando casos...</div>
                )}
                {!loadingCases && testCases.map(tc => (
                  <div key={tc.id} className="wizard-case-row">
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{tc.title}</div>
                      <div style={{ fontSize: 11, color: '#6c757d' }}>{tc.folder_path || tc.folder_name || 'MAIN'}</div>
                    </div>
                    {tc.priority && (
                      <span className="status-badge status-unknown" style={{ textTransform: 'capitalize' }}>{tc.priority}</span>
                    )}
                  </div>
                ))}
                {!loadingCases && testCases.length === 0 && (
                  <div style={{ padding: 16, fontSize: 13, color: '#6c757d' }}>
                    Este ciclo ainda não tem casos de teste vinculados.
                  </div>
                )}
              </div>

              <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                Adicionar casos de teste existentes
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 10, marginBottom: 10 }}>
                <input
                  type="text"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  placeholder="Buscar casos do projeto..."
                />
                <select
                  value={addFolderFilter}
                  onChange={(e) => setAddFolderFilter(e.target.value)}
                >
                  <option value="">Todas as pastas</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11.5, color: '#6c757d' }}>{addableCases.length} casos encontrados</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn-link" onClick={selectAllVisible} disabled={addableCases.length === 0} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                    Selecionar todos
                  </button>
                  <button type="button" className="btn-link" onClick={clearVisibleSelection} disabled={selectedToAdd.size === 0} style={{ background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                    Limpar seleção
                  </button>
                </div>
              </div>
              <div className="wizard-case-preview" style={{ marginBottom: 10 }}>
                {loadingAvailable && (
                  <div style={{ padding: 16, fontSize: 13, color: '#6c757d' }}>Carregando casos do repositório...</div>
                )}
                {!loadingAvailable && addableCases.map(tc => (
                  <label key={tc.id} className="wizard-case-row" style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={selectedToAdd.has(tc.id)}
                        onChange={() => toggleSelectToAdd(tc.id)}
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{tc.title}</div>
                        <div style={{ fontSize: 11, color: '#6c757d' }}>{tc.folder_name || 'MAIN'}</div>
                      </div>
                    </div>
                  </label>
                ))}
                {!loadingAvailable && addableCases.length === 0 && (
                  <div style={{ padding: 16, fontSize: 13, color: '#6c757d' }}>
                    {(addSearch || addFolderFilter) ? 'Nenhum caso encontrado.' : 'Todos os casos do projeto já estão neste ciclo.'}
                  </div>
                )}
              </div>
              <button
                className="btn-secondary"
                onClick={handleAddSelectedCases}
                disabled={selectedToAdd.size === 0 || addingCases}
              >
                {addingCases ? 'Adicionando...' : `Adicionar ${selectedToAdd.size > 0 ? `(${selectedToAdd.size})` : ''}`}
              </button>
            </>
          )}

          {step === 3 && (
            <div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Nome da execução</span>
                <span className="wizard-summary-value">{executionName}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Projeto</span>
                <span className="wizard-summary-value">{projectName}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Ciclo</span>
                <span className="wizard-summary-value">{cycleName}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Casos de teste</span>
                <span className="wizard-summary-value">{testCases.length} casos</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="modal-footer">
        {step === 1 ? (
          <button className="btn-danger" onClick={onCancel}>Cancelar</button>
        ) : (
          <button className="btn-secondary" onClick={() => setStep(step - 1)}>← Voltar</button>
        )}
        <span style={{ flex: 1 }} />
        {step < 3 ? (
          <button className="btn-primary" onClick={handleNext}>Próximo →</button>
        ) : (
          <button className="btn-primary" onClick={handleCreate} disabled={creating || testCases.length === 0}>
            {creating ? 'Criando...' : 'Criar Execução'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateExecutionWizard;
